import { useEffect, useRef, useState } from "react";
import styles from "../../onboarding/questions/InterestsQuestion.module.css";

interface MultiSelectDropdownProps {
	options: string[];
	selectedValues: string[];
	onSelectionChange: (values: string[]) => void;
	placeholder: string;
	getLabelForValue: (value: string) => string;
	maxSelections?: number;
	isRTL?: boolean;
	id?: string;
	label?: string;
}

export function MultiSelectDropdown({
	options,
	selectedValues,
	onSelectionChange,
	placeholder,
	getLabelForValue,
	maxSelections,
	isRTL = false,
	id,
	label,
}: MultiSelectDropdownProps) {
	const [showDropdown, setShowDropdown] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setShowDropdown(false);
			}
		};

		if (showDropdown) {
			document.addEventListener("mousedown", handleClickOutside);
			return () =>
				document.removeEventListener("mousedown", handleClickOutside);
		}
	}, [showDropdown]);

	const handleOptionSelect = (option: string) => {
		let newSelectedValues: string[];

		if (selectedValues.includes(option)) {
			// Remove if already selected
			newSelectedValues = selectedValues.filter((v) => v !== option);
		} else {
			// Check if we're at the limit (if maxSelections is set)
			if (maxSelections && selectedValues.length >= maxSelections) {
				return; // Don't allow more selections
			}

			// Add if not selected
			newSelectedValues = [...selectedValues, option];
		}

		onSelectionChange(newSelectedValues);
	};

	const isOptionSelected = (option: string) => selectedValues.includes(option);

	// Format display text for dropdown trigger
	const getDisplayText = () => {
		if (selectedValues.length === 0) {
			return placeholder;
		}
		const localizedLabels = selectedValues.map(getLabelForValue);
		return localizedLabels.join(", ");
	};

	return (
		<div className={styles.container} dir={isRTL ? "rtl" : "ltr"}>
			<div className={styles.content}>
				{label && (
					<label className={styles.label} htmlFor={id}>
						{label}
					</label>
				)}
				<div className={styles.dropdown} ref={dropdownRef}>
					<button
						type="button"
						onClick={() => setShowDropdown(!showDropdown)}
						className={`${styles.trigger} ${showDropdown ? styles.triggerOpen : ""}`}
						id={id}
					>
						<span
							className={
								selectedValues.length > 0
									? styles.selectedText
									: styles.placeholder
							}
						>
							{getDisplayText()}
						</span>
						<svg
							className={`${styles.chevron} ${showDropdown ? styles.chevronOpen : ""}`}
							width="14"
							height="8"
							viewBox="0 0 14 8"
							fill="none"
							aria-hidden="true"
						>
							<path
								d="M1 1L7 7L13 1"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>

					{showDropdown && (
						<div className={styles.menu}>
							<div className={styles.optionsList}>
								{options.map((option, index) => (
									<div key={option}>
										<button
											type="button"
											onClick={() => handleOptionSelect(option)}
											disabled={
												!isOptionSelected(option) &&
												!!maxSelections &&
												selectedValues.length >= maxSelections
											}
											className={`${styles.option} ${
												!isOptionSelected(option) &&
												maxSelections &&
												selectedValues.length >= maxSelections
													? styles.optionDisabled
													: ""
											}`}
										>
											<div
												className={`${styles.checkbox} ${
													isOptionSelected(option)
														? styles.checkboxSelected
														: ""
												}`}
											>
												{isOptionSelected(option) && (
													<svg
														className={styles.checkmark}
														width="12"
														height="12"
														viewBox="0 0 20 20"
														fill="currentColor"
														aria-hidden="true"
													>
														<path
															fillRule="evenodd"
															d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
															clipRule="evenodd"
														/>
													</svg>
												)}
											</div>
											<span className={styles.optionLabel}>
												{getLabelForValue(option)}
											</span>
										</button>
										{index < options.length - 1 && (
											<div className={styles.divider} />
										)}
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
